import { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import {
  Activity, AlertTriangle, Database, GitBranch, History, LayoutDashboard, LogOut, Package,
  ScrollText, Server, Settings, Sparkles, Zap, type LucideIcon,
} from 'lucide-react';
import { Button } from '@/deploy/components/ui/Button';
import { DeployActivityBadge } from '@/deploy/components/layout/DeployActivityBadge';
import { useSessionStore } from '@/deploy/stores/session.store';
import { API_BASE, isDeployApiConfigured } from '@/deploy/services/api';
import styles from './EmergencyLayout.module.scss';

interface NavItem { to: string; label: string; icon: LucideIcon; end?: boolean }

/** Pages du Déploiement servies en secours (APIs en tête : c'est là qu'on relance un serveur). */
const NAV: NavItem[] = [
  { to: '/deploiement/apis', label: 'APIs', icon: Zap },
  { to: '/deploiement/environments', label: 'Environnements', icon: Server },
  { to: '/deploiement/database', label: 'Base de données', icon: Database },
  { to: '/deploiement/containers', label: 'Containers MC', icon: Package },
  { to: '/deploiement', label: "Vue d'ensemble", icon: LayoutDashboard, end: true },
  { to: '/deploiement/deployments', label: 'Suivi déploiement', icon: Activity },
  { to: '/deploiement/deploy', label: 'Release', icon: GitBranch, end: true },
  { to: '/deploiement/history', label: 'Historique', icon: History },
  { to: '/deploiement/logs', label: 'Logs', icon: ScrollText },
  { to: '/deploiement/resource-packs', label: 'Resource packs', icon: Sparkles },
  { to: '/deploiement/settings', label: 'Paramètres', icon: Settings },
];

const TITLE = 'Accès de secours : Déploiement';

/**
 * Layout de l'accès de secours : bandeau permanent, en-tête minimal, et garde CICD.
 *
 * Aucun appel au backend du panel : ni auth du panel, ni check-access, ni /api/panel. Seul le CICD
 * est interrogé (session.store du Déploiement, token `recube.token`). Sans session CICD valide,
 * seul le formulaire de connexion CICD s'affiche ; les droits restent ceux que le CICD applique.
 */
export function EmergencyLayout() {
  const isLoggedIn = useSessionStore((s) => s.isLoggedIn);
  const bootstrapping = useSessionStore((s) => s.bootstrapping);
  const bootstrap = useSessionStore((s) => s.bootstrap);

  // Relit la session CICD mémorisée (token valide : on entre sans ressaisir le mot de passe).
  useEffect(() => {
    if (bootstrapping) void bootstrap();
  }, [bootstrapping, bootstrap]);

  useEffect(() => {
    const previous = document.title;
    document.title = TITLE;
    return () => { document.title = previous; };
  }, []);

  let content;
  if (bootstrapping) {
    content = <div className={styles.loading}>Chargement…</div>;
  } else if (!isLoggedIn) {
    content = <EmergencyLogin />;
  } else {
    content = <ProjectOutlet />;
  }

  return (
    <div className={styles.layout}>
      <div className={styles.banner} role="status">
        <AlertTriangle size={16} strokeWidth={2} className={styles.bannerIcon} />
        <span className={styles.bannerText}>
          Accès de secours : seule la section Déploiement est disponible. Le reste du panel demande la
          connexion habituelle.
        </span>
        {/* Lien plein (rechargement) : on quitte le routeur de secours pour celui du panel. */}
        <a href="/" className={styles.bannerLink}>Aller au panel</a>
      </div>

      <header className={styles.header}>
        <div className={styles.brand}>
          <img src="/recube-logo.png" alt="Recube" className={styles.logo} />
          <h1 className={styles.title}>{TITLE}</h1>
        </div>
        {isLoggedIn && <AccountControls />}
      </header>

      {isLoggedIn && (
        <nav className={styles.nav} aria-label="Pages du Déploiement">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) => `${styles.navItem} ${isActive ? styles.navItemActive : ''}`}
            >
              <Icon size={15} strokeWidth={2} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
      )}

      <main className={styles.content}>{content}</main>
    </div>
  );
}

/**
 * Pages du Déploiement, remontées au changement de projet (comme le panel remonte ses pages au
 * changement de jeu) : chaque page relit les données du bon projet.
 */
function ProjectOutlet() {
  const projectId = useSessionStore((s) => s.currentProject().id);
  return <Outlet key={projectId || 'aucun'} />;
}

/** Sélecteur de projet CICD, compte CICD connecté et déconnexion. */
function AccountControls() {
  const projects = useSessionStore((s) => s.projects);
  const projectId = useSessionStore((s) => s.currentProject().id);
  const setProject = useSessionStore((s) => s.setProject);
  const user = useSessionStore((s) => s.user);
  const logout = useSessionStore((s) => s.logout);
  const account = user.name || user.email;

  return (
    <div className={styles.account}>
      <label className={styles.project}>
        <span>Projet</span>
        <select
          value={projectId}
          onChange={(e) => setProject(e.target.value)}
          disabled={projects.length === 0}
        >
          {projects.length === 0 && <option value="">Aucun projet</option>}
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.code ? `${p.name} (${p.code})` : p.name}
            </option>
          ))}
        </select>
      </label>
      <DeployActivityBadge />
      <span className={styles.user} title={user.email || undefined}>
        <span className={styles.userLabel}>Compte CICD</span>
        <strong>{account}</strong>
      </span>
      <button type="button" className={styles.logout} onClick={logout} title="Se déconnecter du CICD">
        <LogOut size={15} strokeWidth={2} />
        <span>Déconnexion</span>
      </button>
    </div>
  );
}

/** Connexion au CICD (e-mail + mot de passe de Déploiement), seule entrée de l'accès de secours. */
function EmergencyLogin() {
  const login = useSessionStore((s) => s.login);
  const apiConfigured = isDeployApiConfigured();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const result = await login(email.trim(), password);
    setLoading(false);
    if (!result.ok) setError(result.reason);
  };

  return (
    <div className={`deploy-scope ${styles.loginWrap}`}>
      <form className={styles.loginCard} onSubmit={onSubmit}>
        <h2 className={styles.loginTitle}>Connexion au CICD</h2>
        <p className={styles.loginText}>
          Utilise ton e-mail et ton mot de passe de Déploiement (recube-cicd). La connexion Recube du
          panel n'est pas utilisée ici.
        </p>

        {!apiConfigured && (
          <div className={styles.loginError} role="alert">
            L'adresse de l'API recube-cicd n'est pas configurée (VITE_DEPLOY_API_URL) : la connexion est
            impossible depuis ce build.
          </div>
        )}

        <label className={styles.field}>
          <span>E-mail</span>
          <input
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="prenom.nom@paladium.games"
          />
        </label>

        <label className={styles.field}>
          <span>Mot de passe</span>
          <input
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>

        {error && <div className={styles.loginError} role="alert">{error}</div>}

        <Button type="submit" variant="primary" size="lg" fullWidth loading={loading} disabled={!apiConfigured}>
          Se connecter
        </Button>

        {apiConfigured && API_BASE && (
          <p className={styles.loginApi}>API : <code>{API_BASE}</code></p>
        )}
      </form>
    </div>
  );
}
